import React, { useEffect, useState } from "react";
import Paths from "./tables/paths";
import Walkthroughs from "./tables/walkthroughs";
import Docs from "./tables/docs";
import { AUDIENCE_GET_ACTIVITY_DETIALS, AUDIENCE_SEND_PATH_NUDGE } from "../../../../endpoints";
import { AuthContext } from "../../../../contexts";
import axios from "axios";
import Loader from "../../../../organisms/loader/Loader";
import { toast } from "react-toastify";
import moment from "moment";

const formatLastRemindedLabel = (lastRemindedAt) => (
  lastRemindedAt ? `Last reminded ${moment(lastRemindedAt).fromNow()}` : "Not reminded yet"
);

function Activity({ profile, onOpenResponses }) {
  const [activityData, setActivityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudgeLoadingByPathId, setNudgeLoadingByPathId] = useState({});
  const [openNudgeMenuPathId, setOpenNudgeMenuPathId] = useState(null);
  const { token } = React.useContext(AuthContext);

  useEffect(() => {
    let isMounted = true;
    const fetchActivityData = async () => {
      try {
        const options = {
          url: `${AUDIENCE_GET_ACTIVITY_DETIALS(profile?.id)}`,
          headers: { Authorization: token }
        };
        const response = await axios.request(options);
        if (isMounted) {
          setActivityData(response.data);
          setOpenNudgeMenuPathId(null);
        }
      } catch (error) {
        console.error("Error fetching file details:", error);
        toast.error("Failed to fetch activity details. Please try again.");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (profile?.id) {
      fetchActivityData();
    } else {
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [profile?.id, token]);

  const handleOpenNudgePopup = (pathRow) => {
    const pathId = pathRow?.learning_material_id;
    if (!pathId) return;

    setOpenNudgeMenuPathId(pathId);
  };

  const handleCloseNudgePopup = () => {
    setOpenNudgeMenuPathId(null);
  };

  const handleSendNudge = async (pathRow, stepOption) => {
    const pathId = pathRow?.learning_material_id;
    if (!profile?.id || !pathId) {
      return;
    }

    handleCloseNudgePopup();
    setNudgeLoadingByPathId((current) => ({
      ...current,
      [pathId]: true
    }));

    try {
      const response = await axios.request({
        url: AUDIENCE_SEND_PATH_NUDGE(profile.id, pathId),
        method: "POST",
        headers: { Authorization: token },
        data: {
          step_uid: stepOption?.step_uid
        }
      });
      const stepLabel = stepOption?.step_number ? `Step ${stepOption.step_number}` : "selected step";
      setActivityData((current) => {
        if (!current?.paths?.data) return current;

        return {
          ...current,
          paths: {
            ...current.paths,
            data: current.paths.data.map((row) => {
              if (row.learning_material_id !== pathId) return row;

              const steps = Array.isArray(row?.nudge?.steps) ? row.nudge.steps : [];
              return {
                ...row,
                nudge: {
                  ...row.nudge,
                  steps: steps.map((step) => (
                    step.step_uid === stepOption?.step_uid
                      ? { ...step, last_reminded_at: response?.data?.sent_at || new Date().toISOString() }
                      : step
                  ))
                }
              };
            })
          }
        };
      });
      toast.success(`Nudge email sent for ${pathRow?.material_name || "this path"} (${stepLabel}).`);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to send nudge email. Please try again.");
    } finally {
      setNudgeLoadingByPathId((current) => ({
        ...current,
        [pathId]: false
      }));
    }
  };

  const activeNudgePath = Array.isArray(activityData?.paths?.data)
    ? activityData.paths.data.find((row) => row.learning_material_id === openNudgeMenuPathId) || null
    : null;
  const activeNudgeSteps = Array.isArray(activeNudgePath?.nudge?.steps) ? activeNudgePath.nudge.steps : [];

  return (
    <div className="activity">
      {loading ? <Loader /> :
        <>
          <Paths
            data={activityData?.paths}
            onViewDetails={onOpenResponses}
            onOpenNudgePopup={handleOpenNudgePopup}
            openNudgeMenuPathId={openNudgeMenuPathId}
            nudgeLoadingByPathId={nudgeLoadingByPathId}
          />
          <Walkthroughs data={activityData?.walkthrough} onViewDetails={onOpenResponses} />
          <Docs data={activityData?.docs} onViewDetails={onOpenResponses} />

          {activeNudgePath && (
            <div className="nudge-popup-backdrop" onClick={handleCloseNudgePopup}>
              <div className="nudge-popup" onClick={(event) => event.stopPropagation()}>
                <div className="nudge-popup-header">
                  <div className="nudge-popup-heading">
                    <p className="nudge-popup-eyebrow">Path</p>
                    <h3>{activeNudgePath.material_name || "Send nudge"}</h3>
                  </div>
                  <button
                    type="button"
                    className="nudge-popup-close"
                    onClick={handleCloseNudgePopup}
                    aria-label="Close nudge popup"
                  >
                    ×
                  </button>
                </div>

                <div className="nudge-popup-list">
                  {activeNudgeSteps.map((step) => (
                    <div
                      key={step.step_uid}
                      className={`nudge-menu-option ${step.can_send ? "" : "is-disabled"}`}
                    >
                      <div className="nudge-menu-option-main">
                        <div className="nudge-menu-option-content">
                          <span className="nudge-menu-option-label">
                            {step.step_number ? `Step ${step.step_number}` : "Step"}
                          </span>
                          <span className="nudge-menu-option-copy">
                            {step.step_title || "Untitled step"}
                          </span>
                          <span className={`nudge-menu-option-meta ${step.can_send ? "" : "is-muted"}`}>
                            {step.can_send ? formatLastRemindedLabel(step.last_reminded_at) : step.reason}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn-avatar-details btn-avatar-details--nudge-action"
                          onClick={() => handleSendNudge(activeNudgePath, step)}
                          disabled={!step.can_send || Boolean(nudgeLoadingByPathId[activeNudgePath.learning_material_id])}
                        >
                          <span className="text-btn-avatar-details">
                            {Boolean(nudgeLoadingByPathId[activeNudgePath.learning_material_id]) && step.can_send ? "Sending..." : "Send"}
                          </span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      }
    </div>
  );
}

export default Activity;
